import { useState, useCallback, useRef } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiToken } from '@/auth/acquireApiToken';
import { getAdminApiUrl } from "../../shared";

export interface RunGeneratorResponse {
  seriesProcessed: number;
  occurrencesCreated: number;
  occurrencesSkippedExisting: number;
  occurrencesSkippedOverride: number;
  seriesFailed: number;
  auroraDivergences: number;
  durationMs: number;
  /** "skipped" when the generator's feature flags are off — nothing ran. */
  status: "ok" | "skipped";
  /** Present when the run was skipped or completed with failures. */
  message?: string;
}

export interface RunGeneratorInput {
  environment: "dev" | "staging" | "prod";
}

export function useRunDealSeriesGenerator() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (input: RunGeneratorInput): Promise<RunGeneratorResponse | null> => {
      const apiUrl = getAdminApiUrl(input.environment);
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return null;
      }
      setLoading(true);
      setError(null);
      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const response = await fetch(`${apiUrl}/admin/deal-series/run`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: "{}",
        });
        const data = await response.json();
        if (!response.ok) {
          setError(data.error || `HTTP ${response.status}`);
          return null;
        }
        return data as RunGeneratorResponse;
      } catch (err: any) {
        setError(err?.message ?? "Unknown error");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance],
  );

  return { run, loading, error };
}
